// Byte-bound Manual drafts. These operations edit exact source spans; they never
// rebuild a block from a mock recipe or confer authority to bypass server QA.
import { editBackground } from './background-fields.js';
export class FieldEditError extends Error {
  constructor(code, message) { super(message); this.code = code; }
}
const fail = (code, message) => { throw new FieldEditError(code, message); };
const escape = (value) => String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const wellFormed = (s) => !/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/u.test(s);

export function fieldReplacement(field, input) {
  if (field?.schema !== 'ArtifactField.v1' || field.editable !== true) fail('field_read_only', 'This field is not editable.');
  const s = String(input ?? '');
  if (!wellFormed(s) || s.length > 12000 || /[\u0000-\u0008\u000b\u000c\u000e-\u001f]/.test(s)) fail('field_invalid', 'The field contains unsupported characters.');
  let value = s;
  if(field.t==='background') {
    if(s===field.value)return {value:s,raw:field.binding.raw};
    try { const result=editBackground(field.base_style,s);return {value:result.value,raw:field.style_mode==='insert'?` style="${result.style}"`:result.style}; }
    catch(error){fail('field_invalid',error.message);}
  }
  if (field.t === 'num') {
    if (!/^\d+(?:\.\d+)?$/.test(s) || !Number.isFinite(Number(s))
      || Number(s) !== field.value && (Number(s) < field.min || Number(s) > field.max)) fail('field_invalid', `Enter a number from ${field.min} to ${field.max}.`);
    value = Number(s);
  } else if (field.t === 'color') {
    if (!/^#(?:[\da-f]{3}|[\da-f]{6}|[\da-f]{8})$/i.test(s)) fail('field_invalid', 'Enter a hex colour such as #FBC600.');
  } else if (field.options) {
    if (!field.options.includes(s)) fail('field_invalid', 'Select one of the allowed values.');
  } else if (field.validation === 'spacing') {
    if (!/^(?:0|\d+(?:\.\d+)?(?:px|%))(?:\s+(?:0|\d+(?:\.\d+)?(?:px|%))){0,3}$/.test(s) || s.split(/\s+/).some(v => parseFloat(v) > 2000)) fail('field_invalid', 'Use one to four non-negative pixel or percentage values (zero may omit its unit).');
  } else if (field.validation === 'font-family') {
    if (!/^[\w ,"'\-]+$/.test(s) || s.length > 200) fail('field_invalid', 'Enter a font-family list, without CSS declarations.');
  } else if (field.validation === 'url') {
    if (!/^(?:https?:\/\/|mailto:|tel:)/i.test(s) || /[\s<>]/.test(s)) fail('field_invalid', 'Enter a normal web, email or telephone URL. Link authority is checked on apply.');
  } else if (field.validation === 'local-asset') {
    if (!/^(?:\.\/)?images\/[A-Za-z0-9][A-Za-z0-9._-]{0,119}$/.test(s) || s.endsWith('\n')) fail('field_invalid', 'Choose an existing image in this revision bundle.');
  } else if (!['text', 'ta'].includes(field.t)) fail('field_invalid', 'Unsupported field type.');
  if (/\{\{|\{%|<%|%%/.test(s)) fail('field_protected', 'Use the governed code/ESP path for merge tokens.');
  const raw = escape(String(value) + (field.t === 'num' ? field.u || '' : ''));
  return { value, raw };
}

export function applyFieldEdits(code, fields, changes) {
  if (!changes || typeof changes !== 'object' || Array.isArray(changes)) fail('field_invalid', 'Expected named field values.');
  const edits = Object.entries(changes).map(([key, input]) => {
    const field = fields?.[key], b = field?.binding;
    if (!b || !Number.isSafeInteger(b.start) || !Number.isSafeInteger(b.end) || b.start < 0 || b.end < b.start || b.end > code.length || code.slice(b.start, b.end) !== b.raw) fail('field_stale', 'The source changed. Apply or reload before using these fields.');
    const replacement = fieldReplacement(field, input);
    // Preserve original escaping, case and precision on an unchanged field.
    return { key, start: b.start, end: b.end, ...replacement, raw: replacement.value === field.value ? b.raw : replacement.raw };
  }).filter(edit => edit.raw !== code.slice(edit.start,edit.end)).sort((a, b) => a.start - b.start);
  for (let i = 1; i < edits.length; i++) if (edits[i].start < edits[i - 1].end) fail('field_overlap', 'These fields overlap; apply them separately.');
  let next = code;
  for (const edit of [...edits].reverse()) next = next.slice(0, edit.start) + edit.raw + next.slice(edit.end);
  const projected = Object.fromEntries(Object.entries(fields).map(([key, field]) => {
    const b = field.binding;
    if (!b) return [key, field];
    const own = edits.find(e => e.key === key);
    if (!own && edits.some(e => e.start < b.end && e.end > b.start)) return [key, { ...field, editable: false, refusal: 'overlapping_source_edit' }];
    const delta = edits.filter(e => e.end <= b.start && e.key !== key).reduce((n, e) => n + e.raw.length - (e.end - e.start), 0);
    const start = b.start + delta, raw = own ? own.raw : b.raw;
    return [key, { ...field, ...(own ? { value: own.value } : {}), binding: { ...b, start, end: start + raw.length, raw } }];
  }));
  return { code: next, fields: projected, changed: next !== code };
}
